import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import { InstitutionLaboratoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as InstitutionLaboratoryActions from "../../../actions/institutionLaboratory";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./InstitutionLaboratoriesPage.scss";

interface InstitutionLaboratoriesPageProps {
    institution: Dtos.Institution;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    institutionLaboratories: Dtos.InstitutionLaboratory[];
    loadingInstitutionLaboratories: boolean;
    loadInstitutionLaboratoriesSuccess: boolean;
    loadInstitutionLaboratoriesFailure: boolean;
    loadInstitutionLaboratoriesErrorCode: string;
    deletingInstitutionLaboratory: boolean;
    deleteInstitutionLaboratorySuccess: boolean;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    clearInstitutionLaboratories: InstitutionLaboratoryActions.IInstitutionLaboratoryClearActionCreator;
    loadInstitutionLaboratories: InstitutionLaboratoryActions.IInstitutionLaboratoryLoadByInstitutionIdActionCreator;
    deleteInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratoryDeleteActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const InstitutionLaboratoriesPage: FC<InstitutionLaboratoriesPageProps> = ({
    institution,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    institutionLaboratories,
    loadingInstitutionLaboratories,
    loadInstitutionLaboratoriesSuccess,
    loadInstitutionLaboratoriesFailure,
    loadInstitutionLaboratoriesErrorCode,
    deletingInstitutionLaboratory,
    deleteInstitutionLaboratorySuccess,
    permissions,
    pageSize,
    clearInstitution,
    loadInstitution,
    clearInstitutionLaboratories,
    loadInstitutionLaboratories,
    deleteInstitutionLaboratory,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [showInActive, setShowInActive] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [labInstitution, setLabInstitution] = useState<
        Dtos.InstitutionLaboratory | undefined
    >(undefined);

    //
    // -- Variables --
    const showInstitutionLaboratories =
        !loadingInstitution && 
        loadInstitutionSuccess && 
        !loadingInstitutionLaboratories && 
        loadInstitutionLaboratoriesSuccess;


    // Load Institution & laboratories
    useEffect(() => {
        if (institutionIdNumber && !institution) {
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );
            setBreadcrumbs(
                InstitutionLaboratoriesBreadcrumbs(institutionIdNumber)
            );

            loadInstitution(institutionIdNumber);
            loadInstitutionLaboratories(institutionIdNumber);
        }
    }, [
        institutionIdNumber,
        institution,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitution,
        loadInstitutionLaboratories,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadInstitutionLaboratoriesFailure) {
            navigate(`/error/${loadInstitutionLaboratoriesErrorCode}`);
        }
    }, [
        loadInstitutionFailure, 
        loadInstitutionErrorCode, 
        loadInstitutionLaboratoriesFailure, 
        loadInstitutionLaboratoriesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution?.name) {
            setBreadcrumbs(
                InstitutionLaboratoriesBreadcrumbs(
                    institutionIdNumber,
                    institution.name
                )
            );
        }
    }, [
        institution,
        institutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the institution on component unmount
    useEffect(() => {
        return () => {
            clearInstitution();
            clearInstitutionLaboratories();
        };
    }, [clearInstitution, clearInstitutionLaboratories]);

    // Reload institution after delete
    useEffect(() => {
        if (
            !deletingInstitutionLaboratory &&
            deleteInstitutionLaboratorySuccess
        ) {
            loadInstitution(institutionIdNumber);
            loadInstitutionLaboratories(institutionIdNumber);
        }
    }, [
        deletingInstitutionLaboratory,
        deleteInstitutionLaboratorySuccess,
        loadInstitution,
        loadInstitutionLaboratories,
        institutionIdNumber,
    ]);

    const generateDeleteButton = (
        institutionLab: Dtos.InstitutionLaboratory
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabInstitutionUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`labDeleteButton-${institutionLab.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setLabInstitution(institutionLab);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        if (
            !showInActive &&
            institutionLaboratories?.filter((x) => x?.active == true)
                ?.length === 0 &&
            institutionLaboratories?.filter((x) => x?.active == false)
                ?.length > 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no active institution
                            laboratories to view.
                        </span>
                    </div>
                </div>
            );
        }

        let tableData = showInActive
            ? institutionLaboratories
            : institutionLaboratories?.filter((x) => x?.active == true);

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no institution laboratories to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no institution laboratories to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/institutions/${institutionIdNumber}/labs/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.FLASKVIAL}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "laboratory.name",
                            Header: "Laboratory",
                            accessor: "laboratory.name",
                        },
                        {
                            id: "dateCeased",
                            Header: "Date Ceased",
                            accessor: "dateCeased",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteDepartment",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        institution,
        institutionLaboratories,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        showInActive,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Laboratories</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        <button
                            onClick={() => {
                                setShowInActive(!showInActive);
                            }}
                            type="button"
                            className="btn btn-outline-dark mr-2"
                            style={{ minWidth: "125px" }}
                        >
                            {showInActive ? "Hide Inactive" : "Show Inactive"}
                        </button>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.LabInstitutionCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/institutions/${institutionIdNumber}/labs/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutionLaboratories ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionLaboratories ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showInstitutionLaboratories,
        createTable,
        setShowInActive,
        showInActive,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabInstitutionUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setLabInstitution(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setLabInstitution(undefined);
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the laboratory "
                            <em>{labInstitution?.laboratory?.name}"</em> from
                            institution <strong>{institution?.name}</strong>.
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteInstitutionLaboratory(labInstitution?.id);
                                setOpenModal(false);
                                setLabInstitution(undefined);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setLabInstitution(undefined);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        labInstitution,
        openModal,
        permissions,
        setOpenModal,
        labInstitution,
        setLabInstitution,
        institutionIdNumber,
        loadInstitution,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabInstitutionView}
            loading={loadingInstitution || loadingInstitutionLaboratories}
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionLaboratoriesPageProps> => {
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let institutionLaboratories: Dtos.InstitutionLaboratory[] | undefined = (
        state.institutionLaboratory.formData instanceof Array
    )
        ? state.institutionLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        institutionLaboratories: institutionLaboratories!,
        loadingInstitutionLaboratories:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status == RequestState.Pending,
        loadInstitutionLaboratoriesSuccess:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status == RequestState.Success,
        loadInstitutionLaboratoriesFailure:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status == RequestState.Failure,
        loadInstitutionLaboratoriesErrorCode: state.institutionLaboratory.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingInstitutionLaboratory:
            state.institutionLaboratory.deleteState &&
            state.institutionLaboratory.deleteState.status ==
                RequestState.Pending,
        deleteInstitutionLaboratorySuccess:
            state.institutionLaboratory.deleteState &&
            state.institutionLaboratory.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionLaboratoriesPageProps> => {
    return {
        loadInstitution: bindActionCreators(
            InstitutionActions.LoadInstitutionById,
            dispatch
        ),
        clearInstitution: bindActionCreators(
            InstitutionActions.Clear,
            dispatch
        ),
        loadInstitutionLaboratories: bindActionCreators(
            InstitutionLaboratoryActions.LoadInstitutionLaboratoryByInstitutionId,
            dispatch
        ),
        clearInstitutionLaboratories: bindActionCreators(
            InstitutionLaboratoryActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.DeleteInstitutionLaboratory,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionLaboratoriesPage);
